import { Note as NoteT } from "@/models/note";
import { Entity, EntityRecord, PartialEntityRecord } from "@/orm/types";
import { getShortId } from "@/utils/id";
import {
  ActionIcon,
  Button,
  Card,
  Group,
  Menu,
  ScrollArea,
  Spoiler,
  Stack,
  Text,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { Link, RichTextEditor } from "@mantine/tiptap";
import Highlight from "@tiptap/extension-highlight";
import Underline from "@tiptap/extension-underline";
import { JSONContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Timestamp, arrayUnion } from "firebase/firestore";
import { useAtomValue } from "jotai";
import { memo, useState } from "react";
import { BsThreeDots } from "react-icons/bs";
import { css } from "vite-plugin-inline-css-modules";
import { Icons } from "../Icons/Icons";

export const NotesWriter = <T extends EntityRecord = EntityRecord>({
  entity,
  propName = "notes",
}: {
  entity: Entity<T>;
  propName?: string;
}) => {
  const { data: item } = useAtomValue(entity.getObserveEntityRecordAtom());

  const propKey = propName as keyof EntityRecord;
  const notes = (item?.[propKey] || []) as NoteT[];

  const onSubmit = async (content: JSONContent) => {
    if (!content) return;

    await entity.update({
      [propName]: arrayUnion({
        id: getShortId(),
        content,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      }),
    } as PartialEntityRecord<T>);
  };

  const removeNote = async (note: NoteT) => {
    if (!item) return;

    await entity.update({
      [propName]: notes.filter((n: NoteT) => n.id !== note.id),
    } as PartialEntityRecord<T>);
  };

  const updateNote = async (noteId: string, content: JSONContent) => {
    if (!item) return;
    const noteIndex = notes.findIndex((note: NoteT) => note.id === noteId);

    if (noteIndex === -1) return;
    const updatedNote = {
      ...notes[noteIndex],
      content,
      updatedAt: Timestamp.now(),
    };

    const updatedNotes = [...notes];
    updatedNotes[noteIndex] = updatedNote;
    await entity.update({
      [propName]: updatedNotes,
    } as PartialEntityRecord<T>);
  };

  if (!item) return null;

  return (
    <ScrollArea
      h={"100%"}
      w={"100%"}
      bg="gray.4"
      className={linkStyles.prettyLinks}
    >
      <Stack
        bg="gray.4"
        w="100%"
        style={{
          borderLeft: "1px solid #eee",
        }}
        p={"sm"}
      >
        <Stack gap={"sm"}>
          <Text fw={600} size="sm">
            Notes
          </Text>

          <Editor onSubmit={onSubmit} />
        </Stack>

        <Stack gap={"sm"}>
          <Stack gap={"sm"}>
            {notes
              .slice()
              .reverse()
              .map((note) => (
                <Note
                  key={note.id}
                  note={note}
                  remove={removeNote}
                  update={updateNote}
                />
              ))}
          </Stack>
        </Stack>
      </Stack>
    </ScrollArea>
  );
};

export const Editor = memo(
  ({
    onSubmit,
    initialContent = null,
    onCancel,
    callToAction = "Add Note",
  }: {
    onSubmit: (content: JSONContent) => void;
    initialContent?: null | JSONContent;
    onCancel?: () => void;
    callToAction?: string;
  }) => {
    const [content, setContent] = useState<JSONContent | null>(initialContent);

    const editor = useEditor({
      extensions: [StarterKit, Underline, Highlight, Link],
      content: content,
      onUpdate({ editor }) {
        setContent(editor.getJSON());
      },
    });

    const onHandleSubmit = () => {
      if (!content) return;

      recursiveConvertUndefinedPropsToNull(content);
      onSubmit(content);
      editor?.commands.clearContent();
    };

    const onHandleCancel = () => {
      editor?.commands.clearContent();
      onCancel && onCancel();
    };

    return (
      <Card p={4}>
        <Stack gap={4}>
          <RichTextEditor editor={editor} variant="subtle">
            <RichTextEditor.Toolbar>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
                <RichTextEditor.Strikethrough />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Link /> <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.ClearFormatting />
              </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>

            <RichTextEditor.Content />
          </RichTextEditor>

          <Group>
            <Button flex={1} onClick={onHandleSubmit}>
              {callToAction}
            </Button>
            {onCancel && (
              <Button color="gray" flex={1} onClick={onHandleCancel}>
                Cancel
              </Button>
            )}
          </Group>
        </Stack>
      </Card>
    );
  },
);

export const Note = ({
  note,
  update,
  remove,
}: {
  note: NoteT;
  update: (noteId: string, content: JSONContent) => Promise<void>;
  remove: (note: NoteT) => Promise<void>;
}) => {
  const [editingText, setEditingText] = useState<null | JSONContent>(null);

  const content = note.content;

  const editor = useEditor({
    editable: false,
    content,
    extensions: [StarterKit, Underline, Highlight, Link],
  });

  const updateNote = async (content: JSONContent) => {
    await update(note.id, content);
    setEditingText(null);
    editor?.commands.setContent(content);
  };

  const removeNote = async (note: NoteT) => {
    await remove(note);
  };

  const openDeleteModal = async () => {
    await modals.openConfirmModal({
      title: "Delete this note?",

      children: (
        <Text size="md">Are you sure you want to delete this note?</Text>
      ),
      labels: {
        confirm: `Delete`,
        cancel: "Cancel",
      },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        removeNote(note);
      },
    });
  };

  const onEdit = () => {
    setEditingText(note.content || note?.text);
  };

  return (
    <Card
      shadow="xs"
      style={{
        border: "1px solid var(--mantine-color-gray-2)",
      }}
      onDoubleClick={onEdit}
      p={0}
    >
      <Stack gap={"sm"}>
        {editingText !== null ? (
          <Editor
            onSubmit={updateNote}
            initialContent={editingText}
            onCancel={() => setEditingText(null)}
            callToAction="Save"
          />
        ) : (
          <>
            <Spoiler
              pl={3}
              maxHeight={92}
              showLabel={
                <Text size="sm" c="gray.7" pl={"md"}>
                  Show More
                </Text>
              }
              hideLabel={
                <Text size="sm" c="gray.7" pl={"md"}>
                  Hide
                </Text>
              }
              styles={{
                control: {
                  width: "100%",
                  //fade up to white with a gradient
                  background: "linear-gradient(rgba(255, 255, 255, 0), white)",

                  position: "absolute",
                  top: "calc(100% - 12px)",
                  height: 45,
                  fontSize: 12,
                  textAlign: "left",
                  textTransform: "uppercase",
                  textDecoration: "underline",
                  padding: "20px 0px",
                },
              }}
            >
              <RichTextEditor
                editor={editor}
                variant="subtle"
                style={{
                  border: "none",
                }}
              >
                <RichTextEditor.Content />
              </RichTextEditor>
            </Spoiler>
            <Group justify="flex-end" align="center" px={"xs"} pb={"xs"}>
              <Text size="xs" c="gray.7">
                {note.createdAt.toDate
                  ? note.createdAt.toDate().toLocaleDateString()
                  : new Date().toLocaleDateString()}
              </Text>

              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <ActionIcon color="gray.7" variant="transparent" size={"xs"}>
                    <BsThreeDots size={12} />
                  </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Item leftSection={<Icons.Edit />} onClick={onEdit}>
                    Edit
                  </Menu.Item>
                  <Menu.Item
                    c={"red.7"}
                    leftSection={<Icons.Delete />}
                    onClick={() => openDeleteModal()}
                  >
                    Delete
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </>
        )}
      </Stack>
    </Card>
  );
};

const recursiveConvertUndefinedPropsToNull = (obj: Record<string, unknown>) => {
  for (const key in obj) {
    if (obj[key] === undefined) {
      obj[key] = null;
    } else if (typeof obj[key] === "object") {
      recursiveConvertUndefinedPropsToNull(obj[key] as Record<string, unknown>);
    }
  }
};

const linkStyles = css`
  .prettyLinks {
    a {
      color: var(--mantine-color-blue-6);
      text-decoration: underline;
      cursor: pointer;
    }
  }
`;
